/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Model Image Studio Main Dashboard View (Task 10.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  useImageStudioStore,
  IMAGE_MODELS,
} from '../stores/useImageStudioStore';
import { useI18nStore } from '../stores/useI18nStore';
import { ImageModelSelector } from '../components/media/ImageModelSelector';
import { AspectRatioSelector } from '../components/media/AspectRatioSelector';
import { StylePresetsGrid } from '../components/media/StylePresetsGrid';
import { ImageGalleryGrid } from '../components/media/ImageGalleryGrid';
import { ImageLightboxModal } from '../components/media/ImageLightboxModal';
import {
  Wand2,
  Sparkles,
  Zap,
  Sliders,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  RotateCcw,
} from 'lucide-react';

interface ImageStudioViewProps {
  onNavigateToInpaint?: () => void;
  onNavigateToOps?: () => void;
}

export const ImageStudioView: React.FC<ImageStudioViewProps> = ({
  onNavigateToInpaint,
  onNavigateToOps,
}) => {
  const { t } = useI18nStore();
  const {
    prompt,
    negativePrompt,
    selectedModel,
    imageCount,
    seed,
    guidanceScale,
    isGenerating,
    isEnhancingPrompt,
    generationProgress,
    generationStage,
    errorMessage,
    setPrompt,
    setNegativePrompt,
    setImageCount,
    setSeed,
    setGuidanceScale,
    enhancePromptWithAI,
    generateImages,
    resetForm,
  } = useImageStudioStore();

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showNegative, setShowNegative] = useState(false);

  const currentModel =
    IMAGE_MODELS.find((m) => m.id === selectedModel) || IMAGE_MODELS[0];
  const totalCost = currentModel.creditCost * imageCount;

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    await generateImages();
  };

  return (
    <div className="space-y-8">
      {/* 1. Header Toolbar Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl border border-surface-border bg-surface shadow-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <h2 className="text-xl font-heading font-extrabold text-foreground">
              {t('media.imageStudioTitle')}
            </h2>
            <span className="inline-flex items-center gap-1 text-[10px] px-2.5 py-0.5 rounded-full font-bold bg-accent/15 text-accent border border-accent/20">
              <Sparkles className="w-3 h-3" />
              <span>{t('media.multiModelAi')}</span>
            </span>
          </div>
          <p className="text-xs text-foreground-muted">
            {t('media.imageStudioSubtitle')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated text-xs font-mono">
            <Zap className="w-4 h-4 text-primary" />
            <span>{t('media.batchCost', { cost: totalCost })}</span>
          </div>
          <button
            type="button"
            onClick={resetForm}
            className="p-2 rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated text-foreground-muted hover:text-foreground transition-colors"
            title={t('media.resetDefaults')}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {errorMessage && (
        <div className="p-4 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-500 text-xs font-medium flex items-center gap-2.5 animate-in fade-in">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* 2. Creation Form Section */}
      <form onSubmit={handleGenerate} className="space-y-6 rounded-3xl border border-surface-border bg-surface p-5 sm:p-7 shadow-sm">
        {/* Model Selector */}
        <ImageModelSelector />

        <hr className="border-surface-border" />

        {/* Prompt Input Box & AI Enhancer */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-heading font-bold text-foreground">
              {t('media.promptLabel')} <span className="text-rose-500">*</span>
            </label>
            <button
              type="button"
              onClick={enhancePromptWithAI}
              disabled={isEnhancingPrompt || !prompt.trim() || isGenerating}
              className="flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold bg-primary/10 hover:bg-primary/20 text-primary border border-primary/20 transition-all disabled:opacity-50"
            >
              <Wand2 className="w-3.5 h-3.5" />
              <span>{isEnhancingPrompt ? t('media.enhancing') : t('media.promptEnhancer')}</span>
            </button>
          </div>

          <div className="relative">
            <textarea
              rows={3}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t('media.promptPlaceholderDetailed')}
              className="w-full p-3.5 text-xs rounded-2xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 leading-relaxed font-sans"
            />
          </div>
        </div>

        {/* Negative Prompt Collapsible */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowNegative(!showNegative)}
            className="flex items-center gap-1.5 text-xs text-foreground-muted hover:text-foreground font-semibold"
          >
            <span>{t('media.negativePromptLabel')}</span>
            {showNegative ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showNegative && (
            <textarea
              rows={2}
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder={t('media.negativePromptPlaceholder')}
              className="w-full p-3 text-xs rounded-xl border border-surface-border bg-surface-elevated text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 leading-relaxed font-mono"
            />
          )}
        </div>

        <hr className="border-surface-border" />

        {/* Aspect Ratio Selector */}
        <AspectRatioSelector />

        <hr className="border-surface-border" />

        {/* Style Presets Grid */}
        <StylePresetsGrid />

        <hr className="border-surface-border" />

        {/* Advanced Settings (Count, Seed, Guidance Scale) */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 text-xs text-foreground-muted hover:text-foreground font-semibold"
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>{t('media.advancedSettings')}</span>
            {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-2xl border border-surface-border bg-surface-elevated/40 animate-in fade-in duration-200">
              {/* Image Count N */}
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-foreground">
                  {t('media.imageCountLabel')}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[1, 2, 4].map((count) => (
                    <button
                      key={count}
                      type="button"
                      onClick={() => setImageCount(count)}
                      className={`py-1.5 text-xs rounded-xl border font-bold text-center transition-colors ${
                        imageCount === count
                          ? 'border-primary bg-primary text-primary-fg'
                          : 'border-surface-border bg-surface text-foreground-muted hover:bg-surface-elevated'
                      }`}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>

              {/* Seed */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-foreground">{t('media.seedLabel')}</label>
                  <button
                    type="button"
                    onClick={() => setSeed(null)}
                    className="text-[10px] text-primary hover:underline font-mono"
                  >
                    {t('media.seed')}
                  </button>
                </div>
                <input
                  type="number"
                  value={seed ?? ''}
                  onChange={(e) => setSeed(e.target.value ? parseInt(e.target.value, 10) : null)}
                  placeholder={t('media.seedPlaceholder')}
                  className="w-full px-3 py-1.5 text-xs rounded-xl border border-surface-border bg-surface text-foreground focus:outline-none font-mono"
                />
              </div>

              {/* Guidance Scale */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-foreground">{t('media.guidanceScaleLabel', { value: guidanceScale })}</label>
                  <span className="text-xs font-mono font-bold text-primary">{guidanceScale}</span>
                </div>
                <input
                  type="range"
                  min="3.5"
                  max="12"
                  step="0.5"
                  value={guidanceScale}
                  onChange={(e) => setGuidanceScale(parseFloat(e.target.value))}
                  className="w-full cursor-pointer accent-primary"
                />
              </div>
            </div>
          )}
        </div>

        {/* Generation Progress Bar */}
        {isGenerating && (
          <div className="space-y-2 p-4 rounded-2xl border border-primary/30 bg-primary/5 animate-pulse">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="text-primary flex items-center gap-2">
                <Sparkles className="w-4 h-4 animate-spin" />
                <span>{generationStage}</span>
              </span>
              <span className="font-mono text-foreground font-bold">{generationProgress}%</span>
            </div>
            <div className="w-full h-2 rounded-full bg-surface-border overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-300 rounded-full"
                style={{ width: `${generationProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Submit Generate Button */}
        <div className="pt-2">
          <button
            type="submit"
            disabled={!prompt.trim() || isGenerating}
            className="w-full py-3.5 px-6 rounded-2xl bg-primary text-primary-fg hover:opacity-90 disabled:opacity-50 text-sm font-heading font-extrabold flex items-center justify-center gap-2.5 transition-all shadow-glow-primary/30 active:scale-[0.99]"
          >
            <Sparkles className="w-4 h-4" />
            <span>
              {isGenerating
                ? t('media.generating')
                : t('media.generateImagesBtn', { count: imageCount })}
            </span>
          </button>
        </div>
      </form>

      {/* 3. Recent Generations Gallery Section */}
      <div className="rounded-3xl border border-surface-border bg-surface p-5 sm:p-7 shadow-sm">
        <ImageGalleryGrid />
      </div>

      {/* 4. Fullscreen Lightbox Modal */}
      <ImageLightboxModal
        onNavigateToInpaint={onNavigateToInpaint}
        onNavigateToOps={onNavigateToOps}
      />
    </div>
  );
};
